import React, { useMemo, type JSX } from 'react'
import { useHistory, useNavigation, useResize } from '@/hooks'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ttab/elephant-ui'
import { calculateViewWidths, minimumSpaceRequired } from '@/navigation/lib'
import type { ContentState, NavigationState, ViewProps } from './types'
import { ViewWrapper } from './components'
import { FaroErrorBoundary } from '@grafana/faro-react'
import { Error } from './views'
import { useIndexedDB } from './datastore/hooks/useIndexedDB'
import { useTranslation } from 'react-i18next'

export const AppContent = (): JSX.Element => {
  const { setActiveView } = useHistory()
  const { state } = useNavigation()
  const idb = useIndexedDB()
  const { t } = useTranslation('app')

  useResize()

  const { components, content } = useMemo(() => {
    return getVisibleContent(state, setActiveView)
  }, [state, setActiveView])

  const views = useMemo(() => {
    return calculateViewWidths(state.viewRegistry, content)
  }, [state, content])

  return (
    <>
      {components.map((Component, n) => {
        const item = content[n]
        const { colSpan } = views[n]

        return (
          <ViewWrapper key={item.viewId} viewId={item.viewId} name={item.name} colSpan={colSpan}>
            <FaroErrorBoundary fallback={(error) => <Error error={error} />}>
              <Component {...item.props} />
            </FaroErrorBoundary>
          </ViewWrapper>
        )
      })}

      <Dialog open={!idb?.isConnected}>
        <DialogContent className='focus-visible:outline-none'>
          <DialogHeader>
            <DialogTitle>{t('systemMessages.reloadWindowTitle')}</DialogTitle>
            <DialogDescription>
              {t('systemMessages.reloadWindowDescription')}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  )
}


function getVisibleContent(state: NavigationState, setActiveView: (viewId: string) => void): {
  components: Array<React.FC<ViewProps>>
  content: ContentState[]
} {
  const content = [...state.content ?? []]
  const components = content.map((c) => state.viewRegistry.get(c.name).component)

  let spaceRequired = minimumSpaceRequired(content, state.viewRegistry)
  if (spaceRequired <= 12) {
    return {
      components,
      content
    }
  }

  // Screen size too small for currently available views, remove overflow
  const isWires = content.every((c) => c.name === 'Wires')
  const newActiveId = content.find((c) => c.viewId === state.active)?.viewId || content[0].viewId

  do {
    // Check if newActiveId is within content after shift
    const shiftedContent = content.slice(1)
    if (!isWires || shiftedContent.some((c) => c.viewId === newActiveId)) {
      components.shift()
      content.shift()
    } else {
      // newActiveId is not within content after shift
      // Perform pop operation
      components.pop()
      content.pop()
    }
    spaceRequired = minimumSpaceRequired(content, state.viewRegistry)
  } while (spaceRequired > 12 && components.length > 1)

  // Get active id, or set it to the leftmost view if the active view did not fit
  const activeId = content.find((c) => c.viewId === state.active)?.viewId || content[0].viewId
  setTimeout(() => {
    if (activeId !== state.active) {
      setActiveView(activeId)
    }
  }, 0)

  return {
    components,
    content
  }
}
